import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/stylesheets/FinalStep.css';
import ReactMarkdown from 'react-markdown';
import { saveLatestReport } from './LatestReportBadge';
import { getApiBaseUrl } from '../services/apiBase';

// ✅ Global layout components
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  EVENT_NAMES,
  PARAM_KEYS,
  setPersistentContext,
  trackEvent,
} from '../analytics';

const CAMPAIGN_STORAGE_KEY = "homehelper_campaign_id";
const AUTO_REPORT_LOCK_TTL_MS = 2 * 60 * 1000;
const AUTO_REPORT_COMPLETE_TTL_MS = 30 * 1000;
const API_BASE_URL = getApiBaseUrl();

function getStoredCampaignId() {
  try {
    const raw = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.value || !parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      return null;
    }
    return parsed.value;
  } catch {
    localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
    return null;
  }
}

export default function FinalStep({
  customerId,
  surveyId,
  categoryKey,
  projectGuid = null,
  zipCode = "",
  uploadedImages = [],
  regionSlug = null,
  regionName = null,
  proximityAnswer = null,
  livesWithinRegionDriveTime = null,
  regionProximityQuestion = null,
}) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [prequalAmount, setPrequalAmount] = useState(null);
  const [featured, setFeatured] = useState(null);
  const outputRef = useRef(null);
  const hasAutoRun = useRef(false);
  const lastSavedReportRef = useRef(null);
  const reportViewTrackedRef = useRef(false);
  const navigate = useNavigate();

  function getAutoReportLockKey() {
    if (!customerId || !surveyId) {
      return null;
    }

    return `hh:auto-report:${customerId}:${surveyId}`;
  }

  function readAutoReportLock() {
    const storageKey = getAutoReportLockKey();

    if (!storageKey) {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      sessionStorage.removeItem(storageKey);
      return null;
    }
  }

  function writeAutoReportLock(nextValue) {
    const storageKey = getAutoReportLockKey();

    if (!storageKey) {
      return;
    }

    try {
      if (!nextValue) {
        sessionStorage.removeItem(storageKey);
        return;
      }

      sessionStorage.setItem(storageKey, JSON.stringify(nextValue));
    } catch {
      // ignore storage errors
    }
  }

  function getPreferredZipCode() {
    if (zipCode) {
      return zipCode;
    }

    try {
      return localStorage.getItem("hh_zip") || localStorage.getItem("user_zip") || "";
    } catch {
      return "";
    }
  }

  useEffect(() => {
    outputRef.current?.scrollTo({
      top: outputRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [text]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('financing_data');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.estimatedApproval === 'number') {
        setPrequalAmount(parsed.estimatedApproval);
      }
    } catch (err) {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('featured');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.is_featured) {
        setFeatured(parsed);
      }
    } catch (err) {
      // ignore storage errors
    }
  }, []);

  async function handleSubmit({ source = 'manual' } = {}) {
    if (!customerId || loading) return;

    if (source === 'auto') {
      const existingLock = readAutoReportLock();
      const now = Date.now();

      if (existingLock?.status === 'inflight' && now - Number(existingLock.startedAt || 0) < AUTO_REPORT_LOCK_TTL_MS) {
        return;
      }

      if (existingLock?.status === 'completed' && now - Number(existingLock.completedAt || 0) < AUTO_REPORT_COMPLETE_TTL_MS) {
        return;
      }

      writeAutoReportLock({
        status: 'inflight',
        startedAt: now,
      });
    }

    setLoading(true);
    setText('');

    try {
      trackEvent(EVENT_NAMES.AI_REPORT_GENERATE_STARTED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
        [PARAM_KEYS.PROJECT_GUID]: projectGuid,
        [PARAM_KEYS.HAS_PHOTOS]: uploadedImages.length > 0,
        [PARAM_KEYS.PHOTO_COUNT]: uploadedImages.length || undefined,
        [PARAM_KEYS.REPORT_TYPE]: 'project_summary',
      });

      const response = await fetch(`${API_BASE_URL}/api/ai/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          surveyId,
          categoryKey,
          entryContext: {
            region_slug: regionSlug || null,
            region_name_snapshot: regionName || null,
            answer: proximityAnswer || null,
            lives_within_region_drive_time:
              typeof livesWithinRegionDriveTime === "boolean"
                ? livesWithinRegionDriveTime
                : null,
            region_proximity_question: regionProximityQuestion || null,
          },
        })
      });

      if (!response.ok || !response.body) {
        throw new Error(`Report generation failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        setText(prev => prev + decoder.decode(value, { stream: true }));
      }

      trackEvent(EVENT_NAMES.AI_REPORT_GENERATED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
        [PARAM_KEYS.PROJECT_GUID]: projectGuid,
        [PARAM_KEYS.HAS_PHOTOS]: uploadedImages.length > 0,
        [PARAM_KEYS.PHOTO_COUNT]: uploadedImages.length || undefined,
        [PARAM_KEYS.REPORT_TYPE]: 'project_summary',
      });

      if (source === 'auto') {
        writeAutoReportLock({
          status: 'completed',
          completedAt: Date.now(),
        });
      }
    } catch (error) {
      if (source === 'auto') {
        writeAutoReportLock(null);
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }

  // ✅ AUTO RUN ON FIRST LOAD
  useEffect(() => {
    if (
      customerId &&
      !hasAutoRun.current &&
      !loading &&
      text === ''
    ) {
      hasAutoRun.current = true;
      handleSubmit({ source: 'auto' }).catch((error) => {
        console.error('Failed to auto-generate report:', error);
      });
    }
  }, [customerId, loading, text]);

  useEffect(() => {
    if (!customerId || loading || !text.trim()) {
      return;
    }

    const reportUrl = `/report/${customerId}`;
    const saveKey = `${projectGuid || customerId}:${reportUrl}:${text.length}`;

    if (lastSavedReportRef.current === saveKey) {
      return;
    }

    saveLatestReport({
      projectGuid,
      reportUrl,
      projectType: categoryKey,
      title: 'Project Summary Report',
      savedAt: new Date().toISOString()
    });
    lastSavedReportRef.current = saveKey;
  }, [categoryKey, customerId, loading, projectGuid, text]);

  useEffect(() => {
    if (loading || !text.trim() || reportViewTrackedRef.current) {
      return;
    }

    reportViewTrackedRef.current = true;

    trackEvent(EVENT_NAMES.REPORT_VIEWED, {
      [PARAM_KEYS.SURVEY_ID]: surveyId,
      [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
      [PARAM_KEYS.PROJECT_GUID]: projectGuid,
      [PARAM_KEYS.HAS_PHOTOS]: uploadedImages.length > 0,
      [PARAM_KEYS.PHOTO_COUNT]: uploadedImages.length || undefined,
      [PARAM_KEYS.REPORT_TYPE]: 'project_summary',
      [PARAM_KEYS.PLACEMENT]: 'survey_complete_report',
    });
  }, [categoryKey, loading, projectGuid, surveyId, text, uploadedImages.length]);

  async function handleFinalSubmit() {
    const campaignId = getStoredCampaignId();

    if (campaignId) {
      setPersistentContext({
        [PARAM_KEYS.CAMPAIGN_ID]: campaignId,
      });
    }

    await handleSubmit({ source: 'manual' });
  }

  function handleSaveReport() {
    console.log('Save report clicked');
  }

  // ✅ PATCHED: Immediate navigation (no backend call here)
  function handleCostEstimatorClick() {
    trackEvent(EVENT_NAMES.CTA_CLICK, {
      [PARAM_KEYS.CTA_NAME]: 'calculate_project_cost',
      [PARAM_KEYS.PLACEMENT]: 'report_sidebar',
      [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
      [PARAM_KEYS.SURVEY_ID]: surveyId,
    });

    navigate('/tools/cost-estimator', {
      state: {
        customerId,
        surveyId,
        categoryKey
      }
    });
  }

  function handleFeaturedProfileClick() {
    const providerSlug = featured?.contractor?.provider_slug;
    if (!providerSlug) return;

    trackEvent(EVENT_NAMES.FEATURED_CONTRACTOR_CLICK, {
      [PARAM_KEYS.PROVIDER_ID]: providerSlug,
      [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
      [PARAM_KEYS.ZIP_CODE]: getPreferredZipCode(),
      [PARAM_KEYS.PLACEMENT]: 'report_featured_contractor',
    });

    const nextWindow = window.open(
      `/providers/${providerSlug}`,
      '_blank',
      'noopener,noreferrer'
    );

    if (nextWindow) {
      nextWindow.opener = null;
    }
  }

  function handleBrowseProvidersClick() {
    const preferredZip = getPreferredZipCode();
    const nextParams = new URLSearchParams();

    if (preferredZip) {
      nextParams.set('zip', preferredZip);
    }

    if (categoryKey) {
      nextParams.set('category', categoryKey);
    }

    trackEvent(EVENT_NAMES.BROWSE_PROVIDERS_CLICK, {
      [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
      [PARAM_KEYS.ZIP_CODE]: preferredZip || undefined,
      [PARAM_KEYS.PLACEMENT]: 'report_sidebar',
    });

    navigate(`/providers?${nextParams.toString()}`);
  }

  return (
    <>
      <Header />

      <div className="final-step">
        <h2 className="final-step-header">Project Summary Report</h2>

        {prequalAmount ? (
          <div className="final-step-prequal">
            You are pre-qualified for up to ${prequalAmount.toLocaleString()}.
          </div>
        ) : null}

        <p className="ai-benefits">
          Your personalized Project Summary Report outlines your project, highlights risks,
          and helps you make smarter renovation decisions before speaking with contractors.
        </p>

        <div className="final-two-column">

          <div className="final-left">

            {uploadedImages.length > 0 && (
              <div className="image-gallery">
                {uploadedImages.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Uploaded ${idx + 1}`}
                    className="report-image"
                  />
                ))}
              </div>
            )}

            <div ref={outputRef} className="report-output">
              <ReactMarkdown>{text}</ReactMarkdown>
              {loading && <span className="caret">▌</span>}
            </div>

          </div>

          <div className="final-right">
            <div className="featured-widget">
              <h3>What's Next?</h3>
              <p style={{ marginTop: '5px', marginBottom: '10px' }}>
                Ready to connect with trusted professionals in your area?
              </p>

              {featured?.contractor && (
                <div className="featured-widget-pro">
                  <div className="featured-widget-pro-copy">
                    <p className="featured-widget-pro-label">Recommended pro</p>
                    <div className="featured-widget-pro-heading">
                      {featured.contractor.image_url && (
                        <img
                          className="featured-widget-pro-logo"
                          src={featured.contractor.image_url}
                          alt=""
                        />
                      )}
                      <p className="featured-widget-pro-name">
                        {featured.contractor.company_name || featured.contractor.provider_name}
                      </p>
                    </div>
                  </div>
                  <a
                    className="featured-widget-pro-link"
                    href={`/providers/${featured.contractor.provider_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => {
                      event.preventDefault();
                      handleFeaturedProfileClick();
                    }}
                  >
                    View contractor profile
                  </a>
                </div>
              )}

              <button className="widget-button" onClick={handleBrowseProvidersClick}>
                View Local Contractors
              </button>

              {/* ✅ Estimate Button */}
              {text && !loading && (
                <button
                  className="widget-button"
                  onClick={handleCostEstimatorClick}
                >
                  Refine This Budget Range →
                </button>
              )}

              {text && !loading && (
                <div className="final-step-footer-action">
                  <button
                    className="final-step-button"
                    onClick={handleSaveReport}
                  >
                    Save my report now
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
}
